import { Daum } from '@/@types/Affiliates';
import { RequestedOnMarketplaceEnum } from '@/@types/Enums/marketplace';
import { ProductDetailsResponse, ProductRole } from '@/@types/Products';
import Configurate from '@/assets/icons/Configurate';
import Users from '@/assets/icons/Users';
import Badge from '@/components/shared/Badge/Badge';
import AffiliateLinksDialog from '@/components/shared/AffiliateLinksDialog/AffiliateLinksDialog';
import Divider from '@/components/shared/Divider/Divider';
import { Input } from '@/components/shared/Input/Input';
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import useModalStore from '@/store/useModalStore';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  Copy,
  Edit,
  HelpCircle,
  Link as LinkIcon,
  Lock,
  MoreHorizontal,
  Search,
  Settings2,
  Unlink,
  X,
  Handshake,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router-dom';
import { useAcceptInvite, useDenyInvite } from '../hooks/useAcceptOrDenyInvite';
import { useFetchAffiliates } from '../hooks/useFetchAffiliates';
import { useRemoveAffiliate } from '../hooks/useRemoveAffiliate';
import { mapApiLinksToPromotionLinks } from '@/utils/affiliatePromotionLinkHelpers';
import { Currency } from '@/store/useCurrencyStore';
import { EmptyState } from './EmptyState';
import SideQuickEdit from './SideQuickEdit';
import { usePermissions } from '@/auth/PermissionsContext';

interface Props {
  active: boolean;
  requestedOnMarketplace?: RequestedOnMarketplaceEnum;
}

export default function AffiliateList({
  active,
  requestedOnMarketplace,
}: Props) {
  const { id } = useParams<{ id: string }>();
  const {
    affiliatesData,
    isLoading: isLoadingAffiliates,
    pagination,
    setCurrentPage,
    query,
    setQuery,
    setStatus,
    refetch,
  } = useFetchAffiliates(id || '');

  const { t } = useTranslation(['affiliates', 'common']);
  const { openModal } = useModalStore();
  const [selectedAffiliate, setSelectedAffiliate] = useState<Daum | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [approveModalAffiliateId, setApproveModalAffiliateId] = useState<
    string | null
  >(null);
  const [denyModalAffiliateId, setDenyModalAffiliateId] = useState<
    string | null
  >(null);
  const [linksModalAffiliateId, setLinksModalAffiliateId] = useState<
    string | null
  >(null);
  const { product } = useOutletContext<{ product: ProductDetailsResponse }>();

  const { hasPermission } = usePermissions();
  const isProducer = product?.relationship?.role === ProductRole.PRODUCER;
  const canConfigureProgram = isProducer && hasPermission('affiliates.configure_affiliate_program');
  const canCopyInviteLink = hasPermission('affiliates.copy_affiliate_invite_link');
  const canApproveReject = isProducer && hasPermission('affiliates.approve_reject_affiliate');
  const canEditCommission = isProducer && hasPermission('affiliates.edit_affiliate_commission');
  const canRemove = isProducer && hasPermission('affiliates.remove_affiliate');

  const { mutate: removeAffiliate, isPending: isRemoving } =
    useRemoveAffiliate();
  const { mutate: acceptInvite, isPending: isAccepting } = useAcceptInvite();
  const { mutate: denyInvite, isPending: isDenying } = useDenyInvite();

  const queryClient = useQueryClient();

  const isMarketplacePending =
    requestedOnMarketplace === RequestedOnMarketplaceEnum.PENDING;
  const isMarketplaceRejected =
    requestedOnMarketplace === RequestedOnMarketplaceEnum.REJECTED;

  const handleEdit = (invite: Daum) => {
    setSelectedAffiliate(invite);
    setIsEditing(true);
  };

  const handleAcceptInvite = (productInvitationId: string) => {
    acceptInvite(productInvitationId, {
      onSuccess: () => {
        refetch();
        setApproveModalAffiliateId(null);
      },
    });
  };

  const handleDenyInvite = (productInvitationId: string) => {
    denyInvite(productInvitationId, {
      onSuccess: () => {
        refetch();
        setDenyModalAffiliateId(null);
      },
    });
  };

  const handleRemoveAffiliate = (productInvitationId: string) => {
    if (!productInvitationId) {
      toast({
        variant: 'error',
        description: t('affiliate_removed_error'),
      });
      return;
    }

    removeAffiliate(productInvitationId, {
      onSuccess: () => {
        refetch();
        setSelectedAffiliate(null);
      },
    });
  };

  const HeaderWithTooltip = ({
    title,
    description,
  }: {
    title: string;
    description: string;
  }) => (
    <div className='flex items-center gap-2'>
      <span className='uppercase'>{title}</span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className='h-4 w-4 text-muted-foreground cursor-help' />
          </TooltipTrigger>
          <TooltipContent className='max-w-[300px] px-4 py-3 !overflow-visible'>
            <p
              className='text-xs text-muted leading-relaxed break-words whitespace-normal'
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
            >
              {description}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  const headers = [
    { title: t('user'), key: 'user' },
    { title: t('commission'), key: 'commission' },
    {
      title: (
        <HeaderWithTooltip
          title={t('gross_revenue')}
          description={t('gross_revenue_description')}
        />
      ),
      key: 'gross_revenue',
    },
    {
      title: (
        <HeaderWithTooltip
          title={t('total_commission')}
          description={t('total_commission_description')}
        />
      ),
      key: 'total_commission',
    },
    {
      title: (
        <HeaderWithTooltip
          title={t('producer_profit')}
          description={t('producer_profit_description')}
        />
      ),
      key: 'producer_profit',
    },
    { title: t('links'), key: 'links' },
    { title: t('status'), key: 'status' },
    { title: '', key: 'actions' },
  ];

  const handleCopy = async () => {
    try {
      const currentDomain = window.location.origin;
      const inviteLink = `${currentDomain}/invitation-affiliate/${id}`;
      await navigator.clipboard.writeText(inviteLink);
      toast({
        variant: 'success',
        description: t('invite_link_copied'),
      });
    } catch (err) {
      toast({
        variant: 'error',
        description: t('invite_link_error'),
      });
    }
  };

  const linksModalAffiliate = affiliatesData?.data?.find(
    (affiliate) => affiliate.id === linksModalAffiliateId,
  );

  const promotionLinks = mapApiLinksToPromotionLinks(
    linksModalAffiliate?.invite.links,
    (linksModalAffiliate?.invite.currency as Currency | undefined) ?? 'BRL',
  );

  return (
    <div className='space-y-6'>
      {(isMarketplacePending || isMarketplaceRejected) && (
        <Alert
          variant={isMarketplacePending ? 'warning' : 'destructive'}
          className={isMarketplacePending ? 'border-warning/50 mt-8' : ''}
        >
          <AlertCircle
            className={`h-5 w-5 flex-shrink-0 ${
              isMarketplacePending ? 'text-warning' : 'text-destructive'
            }`}
          />
          <div>
            <AlertTitle
              className={`font-semibold text-sm mb-1 ${
                isMarketplacePending ? 'text-warning' : 'text-destructive'
              }`}
            >
              {isMarketplacePending
                ? t('affiliates:requested_on_marketplace_pending_title')
                : t('affiliates:requested_on_marketplace_rejected_title')}
            </AlertTitle>
            <AlertDescription className='text-sm text-muted-foreground leading-relaxed mt-1'>
              {isMarketplacePending
                ? t('affiliates:requested_on_marketplace_pending_message')
                : t('affiliates:requested_on_marketplace_rejected_message')}
            </AlertDescription>
          </div>
        </Alert>
      )}
      <Card
        className={`w-full overflow-x-auto ${
          !isMarketplacePending && !isMarketplaceRejected ? 'mt-8' : ''
        }`}
      >
        <div className='flex flex-col md:items-center md:flex-row w-full p-4 justify-between max-md:gap-3'>
          <div className='flex items-center gap-3'>
            <h1 className='text-base font-semibold'>{t('affiliates')}</h1>
            <Badge status={active ? 'enabled' : 'disabled'} />
          </div>
          <div
            className={
              (canCopyInviteLink && canConfigureProgram)
                ? 'grid grid-cols-2 max-sm:grid-cols-1 gap-3'
                : ''
            }
          >
            {canCopyInviteLink && (
              <Button
                className={`w-full line-clamp ${
                  active
                    ? 'bg-[#27272A] text-white hover:bg-[#27272A]'
                    : 'bg-[#3A3A3A] text-gray-400 cursor-not-allowed'
                }`}
                disabled={!active}
                onClick={handleCopy}
              >
                {active ? <Copy className='mr-2' /> : <Lock className='mr-2' />}
                <span className='hidden md:block'> {t('copy_invite_link')}</span>
                <span className='block md:hidden'> {t('copy_invite_link')}</span>
              </Button>
            )}
            {canConfigureProgram && (
              <Button
                onClick={() => openModal('config-affiliate')}
                className='w-full text-black'
                data-cy='btn-open-affiliate-config'
              >
                <Settings2 className='mr-2' />
                <span className='hidden md:block'>
                  {' '}
                  {t('configure_program')}
                </span>
                <span className='block md:hidden'>
                  {' '}
                  {t('configure_program')}
                </span>
              </Button>
            )}
          </div>
        </div>

        {active ? (
          <>
            <Divider className='mt-[-4px]' />
            <div className='flex justify-between w-full items-center max-sm:flex-col p-4 gap-4'>
              <Input
                className='w-[302px] max-sm:w-full'
                icon={<Search size={15} />}
                placeholder={t('search_placeholder')}
                value={query}
                onChange={(e) => {
                  setCurrentPage(1);
                  setQuery(e.target.value);
                }}
              />

              <div className='w-fit max-sm:w-full'>
                <Select
                  value={status}
                  onValueChange={(value) => setStatus(value)}
                >
                  <SelectTrigger className='w-[150px] max-sm:w-full'>
                    <SelectValue placeholder={t('all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>{t('all')}</SelectItem>
                    <SelectItem value='active'>{t('active')}</SelectItem>
                    <SelectItem value='pending'>
                      {t('common:pending')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <TableListCustom
              emptyTitleMessage={t('noAffiliates.title')}
              emptyDescriptionMessage={t('noAffiliates.description')}
              emptyIconMessage={<Users />}
              headers={headers}
              isLoadingData={isLoadingAffiliates}
              data={affiliatesData}
              currentPage={pagination?.currentPage}
              setCurrentPage={setCurrentPage}
            >
              {affiliatesData?.data?.map((affiliate) => (
                <TableRow key={affiliate.id} className='hover:bg-transparent'>
                  <TableCell>
                    <div className='flex flex-col gap-0.5'>
                      <span className='text-sm font-medium text-white'>
                        {affiliate.invite.user.name}
                      </span>
                      <span className='text-xs text-gray-400'>
                        {affiliate.email}
                      </span>
                      {affiliate.invite.invited_by_partner && (
                        <div className="flex items-center gap-1.5 mt-1 bg-muted/40 w-fit px-2 py-0.5 rounded-md text-[10px] text-muted-foreground border border-border/50">
                          <Handshake size={12} />
                          <span>
                            {t('affiliates:partner')}:{' '}
                            <span className="text-gray-300 font-medium">
                              {affiliate.invite.invited_by_partner.name}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{affiliate.invite?.commission_value_formatted}</TableCell>
                  <TableCell className='w-[150px]'>
                    {affiliate.invite?.audit?.gross_revenue}
                  </TableCell>
                  <TableCell className='w-[150px]'>
                    {affiliate.invite?.audit?.total_commission}
                  </TableCell>
                  <TableCell className='w-[130px]'>
                    {affiliate.invite?.audit?.producer_profit}
                  </TableCell>
                  <TableCell>
                    {affiliate.invite?.links &&
                    affiliate.invite.links.length > 0 ? (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setLinksModalAffiliateId(affiliate.id)}
                        className='flex items-center gap-2'
                      >
                        <LinkIcon className='h-4 w-4' />
                        {t('view_links') || 'Ver Links'}
                        <span className='ml-1 text-xs'>
                          ({affiliate.invite.links.length})
                        </span>
                      </Button>
                    ) : (
                      <span className='text-muted-foreground text-sm'>
                        {t('no_links') || 'Sem links'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <button
                      className='w-[100px]'
                      onClick={() => setSelectedAffiliate(affiliate)}
                    >
                      <Badge status={affiliate.status} />
                    </button>
                  </TableCell>
                  <TableCell>
                    {affiliate.status === 'pending' ? (
                      canApproveReject ? (
                        <div className='flex gap-2 w-full justify-end'>
                          <Dialog
                            open={denyModalAffiliateId === affiliate.id}
                            onOpenChange={(open) =>
                              open
                                ? setDenyModalAffiliateId(affiliate.id)
                                : setDenyModalAffiliateId(null)
                            }
                          >
                            <DialogTrigger asChild>
                              <Button
                                variant='outline'
                                className='border-transparent'
                                onClick={() =>
                                  setDenyModalAffiliateId(affiliate.id)
                                }
                              >
                                <X className='text-red-500 w-4 h-4' />
                                {t('reject')}
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle className='mb-3'>
                                  {t('reject_affiliate_title')}
                                </DialogTitle>
                                <DialogDescription>
                                  {t('reject_affiliate_description')}
                                </DialogDescription>
                              </DialogHeader>
                              <DialogFooter>
                                <DialogClose>
                                  <Button variant='ghost'>{t('cancel')}</Button>
                                </DialogClose>
                                <Button
                                  className='bg-red-500 hover:bg-red-600'
                                  onClick={() => handleDenyInvite(affiliate.id)}
                                  disabled={isDenying}
                                >
                                  {isDenying
                                    ? t('rejecting')
                                    : t('confirm_reject_affiliate')}
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>

                          <Dialog
                            open={approveModalAffiliateId === affiliate.id}
                            onOpenChange={(open) =>
                              open
                                ? setApproveModalAffiliateId(affiliate.id)
                                : setApproveModalAffiliateId(null)
                            }
                          >
                            <DialogTrigger asChild>
                              <Button
                                variant='outline'
                                className='border-transparent'
                                onClick={() =>
                                  setApproveModalAffiliateId(affiliate.id)
                                }
                              >
                                <Check className='text-primary w-4 h-4' />
                                {t('approve')}
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle className='mb-3'>
                                  {t('approve_affiliate_title')}
                                </DialogTitle>
                                <DialogDescription>
                                  {t('approve_affiliate_description')}
                                </DialogDescription>
                              </DialogHeader>
                              <DialogFooter>
                                <DialogClose>
                                  <Button variant='ghost'>{t('cancel')}</Button>
                                </DialogClose>
                                <Button
                                  className='bg-green-500 hover:bg-green-600'
                                  onClick={() =>
                                    handleAcceptInvite(affiliate.id)
                                  }
                                  disabled={isAccepting}
                                >
                                  {isAccepting
                                    ? t('approving')
                                    : t('confirm_approve_affiliate')}
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </div>
                      ) : null
                    ) : (
                      (canEditCommission || canRemove) ? (
                        <Dialog
                          onOpenChange={(isOpen) =>
                            !isOpen && setSelectedAffiliate(null)
                          }
                        >
                          <div className='flex justify-end'>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <MoreHorizontal className='cursor-pointer' />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className='w-[236px] h-[72px] absolute right-4 -top-16'>
                                {canEditCommission && (
                                  <DropdownMenuItem
                                    className='cursor-pointer w-[228px] h-[32px]'
                                    onClick={() => handleEdit(affiliate)}
                                  >
                                    <Edit />
                                    <DropdownMenuLabel className='text-sm font-normal leading-5 text-left'>
                                      {t('edit')}
                                    </DropdownMenuLabel>
                                  </DropdownMenuItem>
                                )}
                                {canRemove && (
                                  <DialogTrigger className='w-full'>
                                    <DropdownMenuItem
                                      className='cursor-pointer w-[228px] h-[32px]'
                                      onClick={() =>
                                        setSelectedAffiliate(affiliate)
                                      }
                                    >
                                      <Unlink color='#F87171' />
                                      <DropdownMenuLabel className='text-sm font-normal leading-5 text-left'>
                                        {t('delete')}
                                      </DropdownMenuLabel>
                                    </DropdownMenuItem>
                                  </DialogTrigger>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {canRemove && (
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle className='mb-3'>
                                  {t('delete_affiliate_title')}
                                </DialogTitle>
                                <DialogDescription>
                                  {t('delete_affiliate_description')}
                                </DialogDescription>
                              </DialogHeader>
                              <DialogFooter>
                                <DialogClose>
                                  <Button variant='ghost'>{t('cancel')}</Button>
                                </DialogClose>
                                <Button
                                  className='bg-[#F87171] hover:bg-[#f68989]'
                                  type='button'
                                  onClick={() => {
                                    if (selectedAffiliate) {
                                      handleRemoveAffiliate(selectedAffiliate.id);
                                    }
                                  }}
                                  disabled={isRemoving}
                                >
                                  {isRemoving
                                    ? t('removing')
                                    : t('confirm_delete_affiliate')}
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          )}
                        </Dialog>
                      ) : null
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableListCustom>
            {selectedAffiliate && (
              <SideQuickEdit
                affiliate={affiliatesData?.data?.find(
                  (data) =>
                    data.invite.user.id === selectedAffiliate.invite.user.id
                )}
                isOpen={isEditing}
                onClose={async () => {
                  await queryClient.invalidateQueries({
                    queryKey: ['affiliates'],
                  });
                  await refetch();
                  setIsEditing(false);
                }}
              />
            )}
            {linksModalAffiliateId && (
              <AffiliateLinksDialog
                open={linksModalAffiliateId !== null}
                onOpenChange={(open) =>
                  !open && setLinksModalAffiliateId(null)
                }
                links={promotionLinks}
                showCloseButton
              />
            )}
          </>
        ) : (
          <EmptyState
            icon={Configurate}
            title={t('emptyAffiliates.title')}
            description={t('emptyAffiliates.description')}
          />
        )}
      </Card>
    </div>
  );
}
